import exportProgressMixin from "@/mixins/exportProgress";

// Intervalle de sondage du statut, si le flux SSE n'est pas disponible (ms)
const POLL_INTERVAL = 1500;

/**
 * Extraction CSV générée en tâche de fond par le worker Messenger (cf. back
 * DataExportController) : on crée l'extraction, on suit son avancement en direct (flux SSE,
 * repli sur le sondage du statut), puis on télécharge le CSV.
 *
 * Utilisé par la page Extraction et l'export de la facturation automatique. Les étapes et le
 * chronomètre sont ceux du mixin exportProgress, affichés par Global/ExportProgress.
 */
export default {
  mixins: [exportProgressMixin],
  data() {
    return {
      exportId: null,
      exportReady: false,
      exportFilename: null,
      exportRowCount: null,
      downloading: false,
      cancelling: false,
      pollTimer: null,
      streamController: null,
      // Avancement de l'étape en cours (0-100), null tant que le total n'est pas connu
      exportPercent: null,
    };
  },
  beforeDestroy() {
    this.stopExportPolling();
  },
  methods: {
    /** Remet le suivi à zéro : nouvelle extraction à venir, ou modale rouverte. */
    resetDataExport() {
      this.stopExportPolling();
      this.exportId = null;
      this.exportReady = false;
      this.exportFilename = null;
      this.exportRowCount = null;
      this.exportPercent = null;
      this.exportLoading = false;
      this.resetExportProgress();
    },
    /**
     * Crée l'extraction et en suit l'avancement.
     *
     * @param {object} payload      corps de POST data-export (type, et selon l'extraction période ou facturation)
     * @param {string} extractLabel libellé de l'étape serveur
     */
    startDataExport(payload, extractLabel) {
      this.stopExportPolling();
      this.exportLoading = true;
      this.exportReady = false;
      this.exportId = null;
      this.exportRowCount = null;
      this.exportPercent = null;
      const requestId = ++this.exportRequestId;
      this.startExportProgress(extractLabel);
      // Étapes du worker : mise en file, extraction, écriture du CSV
      this.exportSteps = [
        { key: "pending", label: "Mise en file d'attente", state: "active", detail: null },
        { key: "extract", label: extractLabel, state: "pending", detail: null },
        { key: "write", label: "Écriture du fichier CSV", state: "pending", detail: null },
      ];

      this.$http
        .post(process.env.BACK_API_URL + "data-export", payload)
        .then((response) => {
          if (requestId !== this.exportRequestId) {
            return;
          }
          this.exportId = response.data.id;
          if (!this.applyExportStatus(response.data)) {
            this.streamExport(requestId);
          }
        })
        .catch((error) => {
          if (requestId !== this.exportRequestId) {
            return;
          }
          const message = error.response && error.response.data && error.response.data.message;
          this.failExport(message || "L'export du fichier CSV n'a pas pu être lancé");
        });
    },
    /**
     * Suit l'avancement en direct (Server-Sent Events). Lu avec fetch() et non EventSource, qui
     * ne sait pas envoyer le jeton Bearer. Le serveur ferme le flux en fin d'extraction, ou au
     * bout de quelques minutes : on se reconnecte alors. En cas d'erreur, repli sur le sondage.
     */
    streamExport(requestId) {
      const controller = new AbortController();
      this.streamController = controller;
      let finished = false;

      fetch(process.env.BACK_API_URL + "data-export/" + this.exportId + "/events", {
        headers: {
          Accept: "text/event-stream",
          Authorization: "Bearer " + localStorage.getItem("token"),
        },
        signal: controller.signal,
      })
        .then((response) => {
          if (!response.ok || !response.body) {
            throw new Error("Flux SSE indisponible (" + response.status + ")");
          }
          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          let buffer = "";
          const read = () =>
            reader.read().then(({ done, value }) => {
              if (done || requestId !== this.exportRequestId) {
                return;
              }
              buffer += decoder.decode(value, { stream: true });
              // Un évènement SSE se termine par une ligne vide
              let end;
              while ((end = buffer.indexOf("\n\n")) >= 0) {
                const data = buffer
                  .slice(0, end)
                  .split("\n")
                  .filter((line) => line.startsWith("data:"))
                  .map((line) => line.slice(5).trim())
                  .join("\n");
                buffer = buffer.slice(end + 2);
                if (data && this.applyExportStatus(JSON.parse(data))) {
                  finished = true;
                }
              }
              return read();
            });
          return read();
        })
        .then(() => {
          if (!finished && requestId === this.exportRequestId && this.exportLoading) {
            this.streamExport(requestId);
          }
        })
        .catch((error) => {
          if (error.name === "AbortError" || requestId !== this.exportRequestId || !this.exportLoading) {
            return;
          }
          this.pollExport(requestId);
        });
    },
    pollExport(requestId) {
      this.pollTimer = setTimeout(() => {
        this.pollTimer = null;
        this.$http
          .get(process.env.BACK_API_URL + "data-export/" + this.exportId)
          .then((response) => {
            if (requestId === this.exportRequestId && !this.applyExportStatus(response.data)) {
              this.pollExport(requestId);
            }
          })
          .catch(() => {
            // Erreur réseau passagère : on retente au prochain intervalle
            if (requestId === this.exportRequestId) {
              this.pollExport(requestId);
            }
          });
      }, POLL_INTERVAL);
    },
    /**
     * Reporte le statut serveur sur les étapes.
     *
     * @returns {boolean} l'extraction est terminée (fichier prêt ou échec)
     */
    applyExportStatus(exportData) {
      if (exportData.status === "cancelled") {
        this.markExportCancelled();
        return true;
      }
      if (exportData.status === "failed") {
        this.failExport("L'export du fichier CSV a échoué");
        return true;
      }
      if (exportData.status === "done") {
        ["pending", "extract", "write"].forEach((key) => this.setExportStep(key, "done", null));
        this.setExportStep("write", "done", this.formatExportCount(exportData.row_count || 0) + " lignes");
        this.exportPercent = null;
        this.exportFilename = exportData.filename;
        this.exportRowCount = exportData.row_count || 0;
        this.exportReady = true;
        this.exportLoading = false;
        this.stopExportTimer();
        return true;
      }
      if (exportData.status === "running") {
        this.setExportStep("pending", "done");
        const current = exportData.progress_current;
        const total = exportData.progress_total;
        if (exportData.step === "write") {
          this.setExportStep("extract", "done", null);
          this.setExportStep("write", "active", current ? this.formatExportCount(current) + " lignes écrites" : null);
          this.exportPercent = null;
        } else {
          this.setExportStep("extract", "active", total ? this.formatExportCount(current || 0) + " / " + this.formatExportCount(total) : null);
          this.exportPercent = total ? Math.min(100, Math.round(((current || 0) / total) * 100)) : null;
        }
      }
      return false;
    },
    /** Annule l'extraction : le worker l'abandonne au prochain point d'avancement. */
    cancelExport() {
      const requestId = this.exportRequestId;
      this.cancelling = true;
      this.$http
        .post(process.env.BACK_API_URL + "data-export/" + this.exportId + "/cancel")
        .then(() => {
          if (requestId === this.exportRequestId) {
            this.markExportCancelled();
          }
        })
        .catch(() => {
          // 409 : terminée entre-temps, le flux en apporte le statut final
        })
        .finally(() => {
          this.cancelling = false;
        });
    },
    markExportCancelled() {
      if (!this.exportLoading) {
        return;
      }
      this.stopExportPolling();
      const active = this.exportSteps.find((step) => step.state === "active");
      if (active) {
        active.state = "cancelled";
        active.detail = "Annulé";
      }
      this.exportPercent = null;
      this.exportLoading = false;
      this.stopExportTimer();
      this.$toastr.warning("Export du fichier CSV annulé");
    },
    formatExportCount(count) {
      return count.toLocaleString("fr-FR");
    },
    failExport(message) {
      this.failExportStep();
      this.$toastr.error(message);
      this.exportPercent = null;
      this.exportLoading = false;
      this.stopExportTimer();
    },
    /** Arrête le suivi (flux SSE et sondage) : modale fermée ou nouvel export. */
    stopExportPolling() {
      if (this.pollTimer) {
        clearTimeout(this.pollTimer);
        this.pollTimer = null;
      }
      if (this.streamController) {
        this.streamController.abort();
        this.streamController = null;
      }
    },
    downloadExport() {
      this.downloading = true;
      this.$http
        .get(process.env.BACK_API_URL + "data-export/" + this.exportId + "/download", { responseType: "blob" })
        .then((response) => {
          const link = document.createElement("a");
          link.href = URL.createObjectURL(response.data);
          link.download = this.exportFilename || "extraction.csv";
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(link.href);
        })
        .catch(() => {
          this.$toastr.error("Le téléchargement du fichier CSV a échoué");
        })
        .finally(() => {
          this.downloading = false;
        });
    },
  },
};



// WEBPACK FOOTER //
// ./src/mixins/dataExport.js